/**
 * Display types and formatters for projects. Source-of-truth for the
 * shape the projects table, map, and drawers render.
 *
 * Stage labels, descriptions, and tone assignments come from @parcel/types (the
 * canonical taxonomy and presentation tokens, shared with the API/MCP). The
 * theme-aware badge color references come from @parcel/ui.
 */

import {
  DISPOSITION_DEFINITIONS,
  PROJECT_STAGE_DEFINITIONS,
  PROJECT_STAGE_TONES,
} from '@parcel/types';
import { BADGE_TONES, type BadgeTone } from '@parcel/ui';
import type { RecordScores } from './scores.js';
import { formatSignalCalendarDate } from './signal-dates.js';

export type Stage =
  | 'pre_filing'
  | 'filed'
  | 'under_review'
  | 'approved'
  | 'permitted'
  | 'under_construction'
  | 'completed';
export type Disposition = 'active' | 'denied' | 'withdrawn' | 'expired' | 'stalled' | 'cancelled';

type BadgeVariant = 'amber' | 'purple' | 'blue' | 'red' | 'green' | 'gray';

/** Everything a status badge needs: text from @parcel/types, color from here.
 *  Color is either a stock `variant` (+ `dot` class) — used by disposition and the
 *  Unknown fallback — or a shared @parcel/ui palette `tone` (the stage ramp). */
export interface StatusMeta {
  label: string;
  description?: string;
  variant?: BadgeVariant;
  dot?: string;
  tone?: BadgeTone;
}

const DISPOSITION_COLORS: Record<Disposition, { dot: string; variant: BadgeVariant }> = {
  active: { dot: 'bg-green-500', variant: 'green' },
  denied: { dot: 'bg-red-500', variant: 'red' },
  withdrawn: { dot: 'bg-zinc-500', variant: 'gray' },
  expired: { dot: 'bg-zinc-500', variant: 'gray' },
  stalled: { dot: 'bg-amber-500', variant: 'amber' },
  cancelled: { dot: 'bg-red-500', variant: 'red' },
};

const STAGE_TEXT = new Map(PROJECT_STAGE_DEFINITIONS.map((d) => [d.value, d]));
const DISPOSITION_TEXT = new Map(DISPOSITION_DEFINITIONS.map((d) => [d.value, d]));

// A null stage means nothing derived yet (no qualifying signals).
// Render a neutral "Unknown" rather than coercing it to a real value, so the
// column matches what the corresponding filter would (not) select.
const UNKNOWN_META: StatusMeta = { label: 'Unknown', dot: 'bg-zinc-300', variant: 'gray' };

/** Stage badge metadata, with an Unknown fallback for a null (underived) stage. */
export function stageMeta(stage: Stage | null): StatusMeta {
  if (stage == null) return UNKNOWN_META;
  const text = STAGE_TEXT.get(stage);
  return {
    label: text?.label ?? stage,
    description: text?.description,
    tone: PROJECT_STAGE_TONES[stage],
  };
}

/** Fallback marker/legend dot color for a status with no tone (the Unknown stage). */
const NO_TONE_DOT = '#6b7280';

/**
 * CSS color for a stage's map marker AND its legend dot. Single source so the two
 * always match: tones resolve to their shared @parcel/ui accent (a theme CSS var,
 * so it adapts light/dark), and a null/underived stage falls back to neutral gray.
 */
export function stageDotColor(stage: Stage | null): string {
  const { tone } = stageMeta(stage);
  return tone ? BADGE_TONES[tone].dot : NO_TONE_DOT;
}

/** The 7 canonical stages in lifecycle order (map legend / any full stage key). */
export const STAGE_ORDER: Stage[] = [
  'pre_filing',
  'filed',
  'under_review',
  'approved',
  'permitted',
  'under_construction',
  'completed',
];

export interface StageLegendEntry {
  /** Stage value, or 'unknown' for the no-derived-stage bucket. */
  key: string;
  label: string;
  /** Dot color, identical to the project's map marker (see stageDotColor). */
  dot: string;
  count: number;
}

/**
 * Map-legend rows: all 7 stages in lifecycle order (count 0 when none present),
 * plus a trailing "Unknown" row only when some project has no derived stage.
 */
export function buildStageLegend(projects: readonly { stage: Stage | null }[]): StageLegendEntry[] {
  const counts = new Map<Stage, number>();
  let unknown = 0;
  for (const { stage } of projects) {
    if (stage == null) unknown += 1;
    else counts.set(stage, (counts.get(stage) ?? 0) + 1);
  }

  const entries: StageLegendEntry[] = STAGE_ORDER.map((stage) => ({
    key: stage,
    label: stageMeta(stage).label,
    dot: stageDotColor(stage),
    count: counts.get(stage) ?? 0,
  }));
  if (unknown > 0) {
    entries.push({
      key: 'unknown',
      label: stageMeta(null).label,
      dot: stageDotColor(null),
      count: unknown,
    });
  }
  return entries;
}

/** Disposition badge metadata. Disposition is always present (defaults to active). */
export function dispositionMeta(disposition: Disposition): StatusMeta {
  const text = DISPOSITION_TEXT.get(disposition);
  return {
    label: text?.label ?? disposition,
    description: text?.description,
    ...DISPOSITION_COLORS[disposition],
  };
}

/**
 * Visual encoding of disposition on the lifecycle rail (current-dot halo + the
 * upcoming tail). Pure and colocated with the other web-presentation color maps
 * so it stays unit-testable without DOM rendering. Progress (the reached track
 * and reached dots) is always brand green; only the halo and the tail carry
 * disposition.
 */
export interface DispositionRailStyle {
  /** Ready-to-use CSS color (incl. alpha) for the current-dot halo box-shadow. */
  halo: string;
  /** true -> dashed colored tail (terminal/stalled); false -> solid gray (active). */
  tailDashed: boolean;
  /** CSS color for the dashed tail; ignored when tailDashed is false. */
  tailColor: string;
}

export function dispositionRailStyle(disposition: Disposition): DispositionRailStyle {
  switch (disposition) {
    case 'stalled':
      return { halo: 'rgb(245 158 11 / 0.30)', tailDashed: true, tailColor: '#f59e0b' };
    case 'denied':
    case 'cancelled':
      return { halo: 'rgb(220 38 38 / 0.28)', tailDashed: true, tailColor: '#dc2626' };
    case 'withdrawn':
    case 'expired':
      return { halo: 'rgb(113 113 122 / 0.34)', tailDashed: true, tailColor: '#71717a' };
    default:
      // active: halo doubles as the "current" marker; tail stays solid gray.
      return { halo: 'hsl(var(--brand) / 0.18)', tailDashed: false, tailColor: '' };
  }
}

export interface ProjectRow {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  type: string;
  sqft: number | null;
  stage: Stage | null;
  disposition: Disposition;
  developer: string;
  architect: string;
  detectedAt: string;
  source: string;
  sourceBaseUrl: string | null;
  sourceLogoDomain: string | null;
  lat: number;
  lng: number;
  /** This workspace's Atlas Score: `{atlas: {score, bucket, matched}}`; empty when the read carries none. */
  scores?: RecordScores;
}

/** Type label for a project whose use no source has stated. */
export const UNKNOWN_PROJECT_TYPE = 'Unknown';

export function formatSqft(n: number | null) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M sq ft`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k sq ft`;
  return `${n} sq ft`;
}

export function formatDetected(iso: string) {
  return formatSignalCalendarDate(iso);
}
